import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ShieldAlert, Download, Search } from "lucide-react";

const TITLE = "Shared debug report - Inquira";
const DESC = "One-time sanitized debug report shared by an Inquira administrator.";

export const Route = createFileRoute("/debug-report/$token")({
  component: DebugReportPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  errorComponent: ({ error }) => <div role="alert" className="p-6 text-sm">{error instanceof Error ? error.message : String(error)}</div>,
  notFoundComponent: () => <div className="p-6 text-sm">This debug link does not exist.</div>,
});

const REASONS: Record<string, string> = {
  not_found: "This link is not valid.",
  expired: "This link has expired.",
  already_used: "This link has already been opened. Ask the admin for a new one.",
};

interface SharedRun {
  id?: string;
  created_at?: string;
  status?: string;
  http_status?: number | null;
  reason?: string | null;
  detail?: string | null;
  hint?: string | null;
  actor_email?: string | null;
  [k: string]: unknown;
}

function DebugReportPage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [reason, setReason] = useState<string | null>(null);
  const [payload, setPayload] = useState<unknown>(null);
  const consumed = useRef(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const runs: SharedRun[] = useMemo(() => {
    const p = (payload ?? {}) as { recent_runs?: SharedRun[]; selected_run?: SharedRun | null };
    const list = Array.isArray(p.recent_runs) ? [...p.recent_runs] : [];
    if (p.selected_run && !list.some((r) => r.id && r.id === p.selected_run?.id)) list.unshift(p.selected_run);
    return list;
  }, [payload]);

  const filteredRuns = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return runs.filter((r) => {
      if (statusFilter !== "all") {
        if (statusFilter === "success" && r.status !== "success") return false;
        if (statusFilter !== "success" && String(r.http_status ?? "") !== statusFilter) return false;
      }
      if (!needle) return true;
      return JSON.stringify(r).toLowerCase().includes(needle);
    });
  }, [runs, q, statusFilter]);

  useEffect(() => {
    // Single-use link: consume exactly once per mount.
    if (consumed.current) return;
    consumed.current = true;
    (async () => {
      const { data, error } = await supabase.rpc("consume_debug_share" as never, { _token: token } as never);
      if (error) { setState("error"); setReason(error.message); return; }
      const res = (data ?? {}) as { ok?: boolean; reason?: string; payload?: unknown };
      if (!res.ok) { setState("error"); setReason(REASONS[res.reason ?? ""] ?? "This link is no longer available."); return; }
      setPayload(res.payload ?? null);
      setState("ok");
    })();
  }, [token]);

  const download = () => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inquira-debug-report.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8">
      <h1 className="text-xl font-bold">Shared debug report</h1>
      <p className="mt-1 text-sm text-muted-foreground">{DESC}</p>

      {state === "loading" && <p className="mt-6 text-sm">Opening report…</p>}

      {state === "error" && (
        <div className="mt-6 flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <ShieldAlert className="mt-0.5 size-4 text-destructive" />
          <span>{reason}</span>
        </div>
      )}

      {state === "ok" && (
        <div className="mt-6 space-y-3">
          <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            This link has now been used and will not open again.
          </p>
          <button
            onClick={download}
            className="inline-flex items-center gap-2 rounded-lg border border-primary bg-primary/10 px-3 py-1.5 text-sm"
          >
            <Download className="size-4" /> Download JSON
          </button>

          {runs.length > 0 && (
            <div className="rounded-xl border border-border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search runs (reason, detail, actor…)"
                    className="w-full rounded-lg border border-border bg-background py-1.5 pl-7 pr-2 text-xs"
                    data-testid="debug-report-search"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs"
                  data-testid="debug-report-status"
                >
                  <option value="all">All statuses</option>
                  <option value="success">Success</option>
                  <option value="401">401</option>
                  <option value="403">403</option>
                  <option value="500">500</option>
                </select>
                <span className="text-xs text-muted-foreground">
                  {filteredRuns.length} of {runs.length} runs
                </span>
              </div>

              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="py-1 pr-3">When</th>
                      <th className="py-1 pr-3">Status</th>
                      <th className="py-1 pr-3">Reason</th>
                      <th className="py-1 pr-3">Detail</th>
                      <th className="py-1">Actor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRuns.map((r, i) => (
                      <tr key={r.id ?? i} className="border-t border-border/60 align-top">
                        <td className="py-1 pr-3 whitespace-nowrap">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                        </td>
                        <td className="py-1 pr-3">{r.http_status ?? r.status ?? "—"}</td>
                        <td className="py-1 pr-3">{r.reason ?? "—"}</td>
                        <td className="py-1 pr-3 break-all">{r.detail ?? "—"}</td>
                        <td className="py-1">{r.actor_email ?? "—"}</td>
                      </tr>
                    ))}
                    {filteredRuns.length === 0 && (
                      <tr><td colSpan={5} className="py-2 text-muted-foreground">No runs match these filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <pre className="max-h-[60vh] overflow-auto rounded-xl border border-border bg-muted/30 p-3 text-xs">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </div>
      )}
    </main>
  );
}
